import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { buttonVariants } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import type { MondayBoardColumn } from '../../types/integrations';

interface Props {
  label: string;
  value: string[];
  columns: MondayBoardColumn[];
  isLoading: boolean;
  disabled?: boolean;
  onChange: (value: string[]) => void;
}

const MultiColumnSelect = ({ label, value, columns, isLoading, disabled, onChange }: Props) => {
  const [open, setOpen] = useState(false);

  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  const selectedLabels = value.map((id) => columns.find((c) => c.id === id)?.title ?? id).join(', ');

  return (
    <div className="flex items-start gap-3">
      <Label className="w-36 shrink-0 pt-2 text-xs font-semibold uppercase tracking-widest text-text-subtle">
        {label}
      </Label>
      <div className="flex-1 min-w-0">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            disabled={isLoading || columns.length === 0 || disabled}
            className={cn(
              buttonVariants({ variant: 'outline' }),
              'w-full justify-start normal-case font-normal text-left'
            )}
          >
            <span className="truncate">{value.length === 0 ? 'Selecciona columnas visibles' : selectedLabels}</span>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-2" align="start">
            <div className="flex flex-col gap-1 max-h-64 overflow-y-auto">
              {columns.map((col) => (
                <label
                  key={col.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-bg-surface-raised cursor-pointer"
                >
                  <Checkbox checked={value.includes(col.id)} onCheckedChange={() => toggle(col.id)} />
                  <span className="flex-1 text-sm">{col.title}</span>
                  <span className="text-xs text-text-subtle font-mono opacity-60">{col.id}</span>
                </label>
              ))}
            </div>
          </PopoverContent>
        </Popover>
        {value.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {value.map((id) => {
              const col = columns.find((c) => c.id === id);
              return (
                <Badge
                  key={id}
                  variant="secondary"
                  className={`text-xs ${disabled ? '' : 'cursor-pointer'}`}
                  onClick={disabled ? undefined : () => toggle(id)}
                >
                  {col?.title ?? id} ×
                </Badge>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiColumnSelect;
